/**
 * Shared constants + internal helpers for the sypi interface modules.
 *
 * Split out from the monolithic sypi-interface.js so each domain module
 * (init / config / summary / import / errors / schedule / history) can pull
 * only what it needs.
 *
 * `.js` extension preserved to match the parent module and keep inline JSX
 * compatibility with the existing babel/vite setup.
 */

import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";
import get from "lodash/get";
import has from "lodash/has";
import isEqual from "lodash/isEqual";
import { diff } from "deep-object-diff";
import { isObject } from "@utils/input-helpers";
import {
  isEmptyObject,
  stringifyNestedKeyPaths,
} from "@utils/object-helpers";
import { camelToTitle, snakeToCamel } from "@utils/string-helpers";
import { getStore } from "@store";
import { SYPI_S3 } from "@actions/types";
import { setPayload } from "@actions/global";
import {
  setContent,
  setStyleConfig,
  setFeature,
  setBusinessRules,
  setInstance,
  setEntireConfig,
  setSypiDetails,
} from "@features/sypi/actions";

// Maps sypi section names to the routes used by the SyPI form navigation.
export const SYPI_SECTIONS = {
  content: "sypi-content",
  styles: "sypi-style",
  features: "sypi-feature",
  businessRules: "sypi-business-rules",
  instance: "sypi-instance",
};

// Maps sypi section names to the Redux action creators that set that section's
// slice. Used by applyImportedConfig() to dispatch the correct setter.
export const SYPI_SECTION_SETTERS = {
  entire: setEntireConfig,
  content: setContent,
  styles: setStyleConfig,
  features: setFeature,
  businessRules: setBusinessRules,
  instance: setInstance,
};

// --- Schema formatting helpers ------------------------------------------------

function addTitles(obj, accObj = {}) {
  accObj = Object.keys(obj).reduce((acc, cur) => {
    acc[cur] = { ...obj[cur], title: camelToTitle(snakeToCamel(cur)) };
    if (obj[cur] && obj[cur].hasOwnProperty("properties")) {
      let currentProp = obj[cur].properties;
      acc[cur].properties = { ...addTitles(currentProp, acc) };
    }
    return acc;
  }, {});
  return accObj;
}

function addMinLength(obj, accObj = {}) {
  accObj = Object.keys(obj).reduce((acc, cur) => {
    acc[cur] = {
      ...obj[cur],
      ...(obj[cur].hasOwnProperty("type") &&
      (obj[cur].type === "string" ||
        (Array.isArray(obj[cur].type) && !obj[cur].type.includes("null")))
        ? { minLength: 1 }
        : {}),
    };
    if (obj[cur].hasOwnProperty("properties")) {
      let currentProp = obj[cur].properties;
      acc[cur].properties = { ...addMinLength(currentProp, acc) };
    }
    return acc;
  }, {});
  return accObj;
}

export const schemaFormatter = (schemaToFormat) => ({
  ...schemaToFormat,
  definitions: addMinLength(addTitles(schemaToFormat?.definitions)),
  properties: addTitles(schemaToFormat?.properties),
});

// --- Version-load helper (used by setScheduledDraftConfig) ------------------

export const loadSypiVersionDetails = (
  feature,
  sypiPayload,
  store = getStore()
) => {
  const notEmpty = sypiPayload && !isEmptyObject(sypiPayload);
  if (feature === SYPI_S3) {
    const initialConfig = store.getState().sypiNewView.initialConfig;
    let clonedInitialConfig = cloneDeep(initialConfig);
    let updatedSypiConfig = notEmpty
      ? Object.keys(sypiPayload).reduce((accSypiConfig, path) => {
          if (path) {
            set(accSypiConfig, path, sypiPayload[path]);
          }
          return accSypiConfig;
        }, clonedInitialConfig)
      : clonedInitialConfig;
    store.dispatch(setSypiDetails(updatedSypiConfig, false));
  } else {
    store.dispatch(setPayload(notEmpty ? sypiPayload : {}, feature));
  }
};

// --- Summary preview helper -------------------------------------------------

/**
 * Builds the summary-preview payload for reset / import / update flows.
 * Kept module-private (not exported from the barrel) — callers use the
 * higher-level `fetchResetPreview`, `handleSypiImportConfigRequest`, and
 * `handleSypiUpdateConfigRequest` in `sypi-summary` / `sypi-import`.
 *
 * Not exported from the legacy sypi-interface.js either — preserved as an
 * internal helper.
 */
export const getSummaryPreview = async (
  initialConfig,
  diffConfig,
  updatedSypiConfig,
  configToCopy,
  contentSection = null,
  scheduleNewDefaults = false,
  release_version
) => {
  let summaryPreview = {};
  let successFailedImportedKeysReport = {};
  const stringConfigSection = stringifyNestedKeyPaths(diffConfig);
  let sypiConfigToCopy =
    configToCopy === "entire"
      ? cloneDeep(initialConfig)
      : configToCopy
      ? cloneDeep(initialConfig[configToCopy])
      : cloneDeep(updatedSypiConfig);
  sypiConfigToCopy = contentSection
    ? cloneDeep(sypiConfigToCopy[contentSection])
    : sypiConfigToCopy;
  let newConfig =
    configToCopy === "entire"
      ? cloneDeep(initialConfig)
      : configToCopy
      ? cloneDeep(initialConfig[configToCopy])
      : "";

  stringConfigSection.forEach((strPath) => {
    let newValue = get(
      updatedSypiConfig ? updatedSypiConfig : diffConfig,
      strPath
    );
    let fullPath = contentSection
      ? `${configToCopy}.${contentSection}.${strPath}`
      : configToCopy && configToCopy !== "entire"
      ? `${configToCopy}.${strPath}`
      : strPath;

    if (has(sypiConfigToCopy, strPath)) {
      const oldValue = get(
        configToCopy ? sypiConfigToCopy : initialConfig,
        strPath
      );
      if (
        (typeof newValue === typeof oldValue &&
          isObject(newValue) === isObject(oldValue)) ||
        oldValue === undefined
      ) {
        if (!isEqual(newValue, oldValue)) {
          summaryPreview[fullPath] = newValue;
        }
        successFailedImportedKeysReport[fullPath] = { disable: false };
        if (contentSection) {
          set(newConfig, `${contentSection}.${strPath}`, newValue);
        } else if (configToCopy) {
          set(newConfig, strPath, newValue);
        }
      }
    } else {
      summaryPreview[fullPath] = newValue;
      successFailedImportedKeysReport[fullPath] = { disable: true };
    }
  });
  // Deferred import to avoid cycle: setGeneralPreviewSummary lives in
  // sypi-summary, which imports helpers from this module.
  const { setGeneralPreviewSummary } = await import("./sypi-summary");
  setGeneralPreviewSummary(
    SYPI_S3,
    summaryPreview,
    initialConfig,
    successFailedImportedKeysReport,
    scheduleNewDefaults,
    release_version
  );
  return newConfig;
};

// Common diff-helper re-exports so downstream modules import from one place
export { diff };
